import React, { useState } from 'react';
import { updatePassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { BASE_URL } from '../services/api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ChangePasswordPage = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('You must be logged in to change your password.');
        setLoading(false);
        return;
      }

      await updatePassword(user, newPassword);

      // Clear passwordResetRequired. Force-refresh the ID token first — updatePassword can leave
      // the cached one stale, which makes this call fail auth. Best-effort: ProtectedRoute is the
      // source of truth, so a flag left set just bounces the user back here.
      try {
        const idToken = await user.getIdToken(true);
        const response = await fetch(`${BASE_URL}/auth/clear-password-reset`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        if (!response.ok) console.warn('Failed to clear password reset flag:', response.status);
      } catch (clearErr) {
        console.warn('Error clearing password reset flag:', clearErr);
      }

      // Hard navigation on purpose: a client-side navigate reuses ProtectedRoute's stale
      // passwordResetRequired state and bounces straight back here.
      setSuccess(true);
      setTimeout(() => { window.location.assign('/'); }, 1200);
    } catch (err) {
      console.error('Error changing password:', err);
      if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err.code === 'auth/requires-recent-login') {
        setError('For security reasons, please log out and log back in, then try again.');
      } else {
        setError('Failed to change password. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth).catch(() => {});
    window.location.assign('/login');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
        <h2 className="text-2xl font-bold text-center text-gray-900">Set a New Password</h2>
        <p className="text-sm text-center text-gray-600">
          You signed in with a temporary password. Choose a new one to continue.
        </p>

        {success && (
          <p className="text-sm text-center text-green-600 font-medium">
            Password updated. Taking you to the dashboard...
          </p>
        )}
        {error && <p className="text-sm text-center text-red-600 font-medium">{error}</p>}

        <form className="space-y-6" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                disabled={loading || success}
                className="block w-full px-3 py-2 mt-1 pr-10 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm disabled:bg-gray-100"
                placeholder="At least 6 characters"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 mt-1 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
              Confirm password
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              disabled={loading || success}
              className="block w-full px-3 py-2 mt-1 text-gray-900 placeholder-gray-500 border border-gray-300 rounded-md shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-jims-cyan focus:border-jims-cyan sm:text-sm disabled:bg-gray-100"
              placeholder="Re-enter your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </div>

          <button
            type="submit"
            disabled={loading || success}
            className="flex justify-center items-center w-full px-4 py-2 text-sm font-medium text-white bg-jims-cyan border border-transparent rounded-md shadow-sm hover:bg-jims-cyan-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jims-cyan disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>

        <div className="text-center">
          <button
            onClick={handleLogout}
            className="text-sm font-medium text-jims-cyan hover:text-jims-cyan-dark"
          >
            Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordPage;
